import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getListRecipesQueryKey } from "@workspace/api-client-react";
import { Link, useLocation } from "wouter";
import {
  AlertCircle,
  BookOpen,
  CheckCircle2,
  ChevronLeft,
  ClipboardPaste,
  Loader2,
} from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";

const MAX_TEXT_CHARS = 50_000;
const LOADING_MESSAGES = [
  "Reading your recipe…",
  "Sorting ingredients and directions…",
  "Preparing it for review…",
];

interface ExtractTextResponse {
  success?: boolean;
  recipesExtracted?: number;
  recipeIds?: number[];
  error?: string;
}

interface ExtractedRecipeCard {
  id: number;
  title: string;
}

export default function PasteRecipePage() {
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [text, setText] = useState("");
  const [sourceLabel, setSourceLabel] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [multiRecipes, setMultiRecipes] = useState<ExtractedRecipeCard[]>([]);

  const textLength = text.length;
  const isOverLimit = textLength > MAX_TEXT_CHARS;
  const canSubmit = text.trim().length >= 20 && !isOverLimit && !isSubmitting;
  const batchParam = useMemo(
    () => multiRecipes.map((recipe) => recipe.id).join(","),
    [multiRecipes],
  );

  useEffect(() => {
    if (!isSubmitting) return;
    setLoadingMessage(0);
    const timer = window.setInterval(() => {
      setLoadingMessage((current) =>
        Math.min(current + 1, LOADING_MESSAGES.length - 1),
      );
    }, 2400);
    return () => window.clearInterval(timer);
  }, [isSubmitting]);

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (!text.trim()) {
      setError("Paste your recipe text first.");
      textareaRef.current?.focus();
      return;
    }
    if (text.trim().length < 20) {
      setError("That text is too short to identify a recipe.");
      textareaRef.current?.focus();
      return;
    }
    if (isOverLimit) {
      setError(
        `Recipe text must be ${MAX_TEXT_CHARS.toLocaleString()} characters or fewer.`,
      );
      textareaRef.current?.focus();
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setMultiRecipes([]);

    try {
      const response = await fetch("/api/extract/text", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, sourceLabel }),
      });
      const data = (await response.json().catch(() => ({}))) as ExtractTextResponse;
      if (
        !response.ok ||
        !data.success ||
        !data.recipeIds ||
        data.recipeIds.length === 0
      ) {
        throw new Error(
          data.error ?? "Couldn't find a complete recipe in that text.",
        );
      }

      await queryClient.invalidateQueries({
        queryKey: getListRecipesQueryKey(),
      });

      if (data.recipeIds.length === 1) {
        toast({ title: "Recipe extracted! Review it below." });
        navigate(`/recipe/${data.recipeIds[0]}/edit?from=text`);
        return;
      }

      const cards = await Promise.all(
        data.recipeIds.map(async (id) => {
          try {
            const recipeResponse = await fetch(`/api/recipes/${id}`);
            const recipe = (await recipeResponse.json()) as { title?: string };
            return { id, title: recipe.title ?? `Recipe #${id}` };
          } catch {
            return { id, title: `Recipe #${id}` };
          }
        }),
      );
      setMultiRecipes(cards);
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Something went wrong. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (multiRecipes.length > 0) {
    return (
      <Layout>
        <div className="max-w-lg mx-auto w-full px-4 py-8 sm:py-12">
          <div className="text-center mb-7">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-secondary/10 mb-4">
              <CheckCircle2 className="w-7 h-7 text-secondary" />
            </div>
            <h1
              className="text-2xl font-bold text-foreground"
              style={{ fontFamily: "var(--font-display)" }}
            >
              {multiRecipes.length} recipes found
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              Review each recipe before adding it to the cookbook.
            </p>
          </div>

          <div className="space-y-3 mb-6">
            {multiRecipes.map((recipe) => (
              <button
                key={recipe.id}
                type="button"
                onClick={() =>
                  navigate(
                    `/recipe/${recipe.id}/edit?from=text&batch=${batchParam}`,
                  )
                }
                className="w-full flex items-center gap-3 rounded-xl border bg-card p-4 text-left hover:border-primary/40 hover:bg-primary/5 transition-colors"
              >
                <BookOpen className="w-5 h-5 text-primary flex-shrink-0" />
                <span className="font-semibold text-foreground flex-1">
                  {recipe.title}
                </span>
              </button>
            ))}
          </div>

          <div className="flex flex-col gap-3">
            <Button
              size="lg"
              className="w-full"
              onClick={() =>
                navigate(
                  `/recipe/${multiRecipes[0].id}/edit?from=text&batch=${batchParam}`,
                )
              }
            >
              Review All ({multiRecipes.length})
            </Button>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setMultiRecipes([])}
            >
              Paste Another Recipe
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-h-0">
        <div className="border-b bg-background px-4 py-3 flex items-center gap-3">
          <Link
            href="/"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors group"
          >
            <ChevronLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
            Back
          </Link>
          <h1
            className="text-base font-semibold"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Paste Recipe Text
          </h1>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="max-w-2xl mx-auto w-full px-4 py-6 sm:py-8">
            <div className="flex items-start gap-3 mb-6">
              <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <ClipboardPaste className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2
                  className="text-xl font-bold text-foreground"
                  style={{ fontFamily: "var(--font-display)" }}
                >
                  Turn copied text into a recipe
                </h2>
                <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                  Paste everything you have. We’ll sort out the ingredients,
                  directions, equipment, and tips for you to review.
                </p>
              </div>
            </div>

            <div className="space-y-5">
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <Label htmlFor="recipe-text">Recipe text</Label>
                  <span
                    className={`text-xs tabular-nums ${
                      isOverLimit
                        ? "text-destructive font-semibold"
                        : "text-muted-foreground"
                    }`}
                  >
                    {textLength.toLocaleString()} /{" "}
                    {MAX_TEXT_CHARS.toLocaleString()}
                  </span>
                </div>
                <Textarea
                  ref={textareaRef}
                  id="recipe-text"
                  value={text}
                  onChange={(event) => {
                    setText(event.target.value);
                    if (error) setError(null);
                  }}
                  disabled={isSubmitting}
                  autoFocus
                  spellCheck
                  placeholder={`Grandma's Apple Pie\n\nIngredients:\n2 cups flour\n...\n\nDirections:\n1. Preheat the oven...`}
                  className="min-h-[52vh] sm:min-h-[380px] resize-y rounded-xl p-4 text-base leading-relaxed"
                  aria-describedby="recipe-text-help recipe-text-error"
                />
                <p
                  id="recipe-text-help"
                  className={`text-xs leading-relaxed ${
                    textLength > 0 && text.trim().length < 20
                      ? "text-amber-700"
                      : "text-muted-foreground"
                  }`}
                >
                  {textLength > 0 && text.trim().length < 20
                    ? "Paste a little more so we can identify the recipe."
                    : "Include the title, ingredients, and directions when possible. Your pasted text stays here if extraction fails."}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="source-label">
                  Where did it come from?{" "}
                  <span className="font-normal text-muted-foreground">
                    (optional)
                  </span>
                </Label>
                <Input
                  id="source-label"
                  value={sourceLabel}
                  onChange={(event) => setSourceLabel(event.target.value)}
                  disabled={isSubmitting}
                  maxLength={200}
                  placeholder="Family text, Notes app, website name…"
                  className="h-11 rounded-xl"
                />
              </div>

              {error && (
                <div
                  id="recipe-text-error"
                  role="alert"
                  className="flex items-start gap-3 rounded-xl border border-destructive/25 bg-destructive/5 p-4"
                >
                  <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-foreground">
                      Couldn’t extract this recipe
                    </p>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      {error}
                    </p>
                  </div>
                </div>
              )}

              <Button
                size="lg"
                className="w-full h-12 gap-2 rounded-xl text-base"
                disabled={!canSubmit}
                onClick={handleSubmit}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {LOADING_MESSAGES[loadingMessage]}
                  </>
                ) : (
                  <>
                    <ClipboardPaste className="w-5 h-5" />
                    Extract Recipe
                  </>
                )}
              </Button>

              <p className="text-center text-xs text-muted-foreground">
                You’ll review everything before it appears in the cookbook.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}